import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import classNames from "classnames";
import useTranslation from "~/app/hooks/useTranslation";
import { useStoreActions } from "~/store";
import { WorkOrderExcavatorTask } from "~/workorder/types";
import Loader from "~/app/common/Loader";
import Icon, { MaterialIcons } from "~/app/common/Icon";
import Input from "~/app/modules/form/Search/Input";
import { IssueOrServiceType } from "~/app/data/statuses/chat";
import classes from "./style.module.scss";

const PAGE_SIZE = 50;
const SHIMMER_ROWS = 10;

export const taskKey = (task: Pick<WorkOrderExcavatorTask, "type" | "id">) =>
  `${task.type}:${task.id}`;

export type TaskFormProps = {
  machineId: string;
  selectedTasks: WorkOrderExcavatorTask[];
  onSelectedTasksChange: (tasks: WorkOrderExcavatorTask[]) => void;
  disabledTaskKeys?: Set<string>;
};

export type TaskFormComponent = React.FC<TaskFormProps>;

const TaskForm: TaskFormComponent = ({
  machineId,
  selectedTasks,
  onSelectedTasksChange,
  disabledTaskKeys,
}) => {
  const { t } = useTranslation("workorder.create");
  const fetchExcavatorTaskList = useStoreActions((actions) => actions.workOrder.fetchExcavatorTaskList);

  const [excavatorTaskList, setExcavatorTaskList] = useState<WorkOrderExcavatorTask[]>([]);
  const [searchText, setSearchText] = useState("");
  const [initialLoading, setInitialLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  const requestIdRef = useRef(0);

  const selectedKeys = useMemo(
    () => new Set(selectedTasks.map(taskKey)),
    [selectedTasks],
  );

  const loadPage = useCallback(
    async (nextOffset: number, append: boolean, search: string) => {
      if (!machineId) {
        return;
      }

      const requestId = ++requestIdRef.current;
      if (append) {
        setLoadingMore(true);
      } else {
        setInitialLoading(true);
        setHasMore(true);
      }

      try {
        const response = await fetchExcavatorTaskList({
          machineId,
          searchText: search,
          offset: nextOffset,
          types: undefined,
        });

        if (requestId !== requestIdRef.current) {
          return;
        }

        if (!response.success) {
          if (!append) {
            setExcavatorTaskList([]);
          }
          setHasMore(false);
          return;
        }

        const page = response.data ?? [];
        setExcavatorTaskList((prev) => (append ? [...prev, ...page] : page));
        setOffset(nextOffset + page.length);

        if (typeof response.total === "number") {
          setHasMore(nextOffset + page.length < response.total);
        } else {
          setHasMore(page.length === PAGE_SIZE);
        }
      } catch {
        if (requestId === requestIdRef.current && !append) {
          setExcavatorTaskList([]);
          setHasMore(false);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setInitialLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [machineId, fetchExcavatorTaskList],
  );

  useEffect(() => {
    setExcavatorTaskList([]);
    setOffset(0);
    loadPage(0, false, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineId, searchText]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || initialLoading || !hasMore) {
      return;
    }
    loadPage(offset, true, searchText);
  }, [loadingMore, initialLoading, hasMore, loadPage, offset, searchText]);

  const handleTaskStatusChange = useCallback(
    (task: WorkOrderExcavatorTask) => {
      const key = taskKey(task);
      if (disabledTaskKeys?.has(key)) {
        return;
      }
      if (selectedKeys.has(key)) {
        onSelectedTasksChange(selectedTasks.filter((item) => taskKey(item) !== key));
      } else {
        onSelectedTasksChange([...selectedTasks, task]);
      }
    },
    [disabledTaskKeys, onSelectedTasksChange, selectedKeys, selectedTasks],
  );

  const getIconProperties = useCallback(
    (task: WorkOrderExcavatorTask, disabled: boolean) => {
      if (disabled) {
        return {
          name: "radio_button_unchecked" as MaterialIcons,
          className: classes.disabledCheck,
        };
      }
      if (selectedKeys.has(taskKey(task))) {
        return {
          name: "check_circle" as MaterialIcons,
          className: classes.checked,
        };
      }
      return {
        name: "radio_button_unchecked" as MaterialIcons,
        className: classes.unchecked,
      };
    },
    [selectedKeys],
  );

  const renderSubtitle = useCallback(
    (task: WorkOrderExcavatorTask) => {
      const isIssue = task.type === IssueOrServiceType.ISSUE;
      if (isIssue) {
        return `${task.description} - ${task.date}`;
      }
      return t(`taskType.${task.type}`);
    },
    [t],
  );

  return (
    <div className={classes.root}>
      <div className={classes["search-wrap"]}>
        <Input
          autoFocus={false}
          placeholder={t("tasks.search")}
          className={classes.field}
          inputClassName={classes.input}
          onSearch={handleSearch}
          fetching={initialLoading}
          value={searchText}
          iconPosition="left"
        />
      </div>

      {initialLoading && excavatorTaskList.length === 0 ? (
        <div className={classes.shimmerList}>
          {Array.from({ length: SHIMMER_ROWS }).map((_, index) => (
            <div key={index} className={classes.shimmerRow}>
              <div className={classes.shimmerCircle} />
              <div className={classes.shimmerIcon} />
              <div className={classes.shimmerContent}>
                <div className={classes.shimmerTitle} />
                <div className={classes.shimmerSubtitle} />
              </div>
            </div>
          ))}
        </div>
      ) : !excavatorTaskList.length ? (
        <p className={classes["empty-tasks"]}>{t("tasks.empty")}</p>
      ) : (
        <div className={classes.listWrap} id="task-form-scrollable-wrapper">
          <InfiniteScroll
            dataLength={excavatorTaskList.length}
            next={handleLoadMore}
            hasMore={hasMore}
            loader={loadingMore ? <Loader /> : null}
            scrollableTarget="task-form-scrollable-wrapper"
          >
            <div className={classes.list}>
              {excavatorTaskList.map((task) => {
                const key = taskKey(task);
                const disabled = Boolean(disabledTaskKeys?.has(key));
                const icon = getIconProperties(task, disabled);
                const isIssue = task.type === IssueOrServiceType.ISSUE;

                return (
                  <div
                    key={key}
                    className={classNames(classes.row, disabled && classes.rowDisabled)}
                  >
                    <Icon
                      name={icon.name}
                      size={22}
                      className={icon.className}
                      onClick={() => handleTaskStatusChange(task)}
                    />
                    <div
                      className={classNames(
                        classes["type-icon-wrap"],
                        isIssue ? classes.issue : classes.service,
                      )}
                    >
                      <Icon name={isIssue ? "warning" : "build"} />
                    </div>
                    <div className={classes.content}>
                      <p className={classes["task-title"]}>{task.title}</p>
                      <p className={classes["task-type"]}>
                        {renderSubtitle(task)}
                        {disabled ? ` — ${t("tasks.alreadyAdded")}` : ""}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
};

export default TaskForm;
